import twttr from 'twitter-text'
import { css } from 'styled-components'
import { EMOJI_MARK } from './emojiRegex'

const ARABIC_PREVIOUS_RANGE = new RegExp('[\u0626\u0628\u062a-\u062e\u0633-\u0647\u064a]')
const ARABIC_DIACRITICS = new RegExp('[\u064b-\u065f]')
const ARABIC_NEXT_RANGE = new RegExp('[\u0622-\u064a]')
const RTL_CHAR = /[\u0590-\u083F]|[\u08A0-\u08FF]|[\uFB1D-\uFDFF]|[\uFE70-\uFEFF]/gm
const CLOSE_PARENTHESES = /\)/g
const LTR_MARK = '\u200e'
export const RTL_MARK = '\u200f'
const RTL_THRESHOLD = 0.5
const RTL_WORD_LENGTH = 3

interface Entity {
  entityText: string
  entityType: string
}

export const isRightToLeftText = (text?: string) => {
  if (!text) {
    return false
  }

  if (!text.match(RTL_CHAR)) {
    return false
  }

  const terms = text.trim().split(/\s+/)
  const mentions = twttr.extractMentionsWithIndices(text)
  const urls = twttr.extractUrlsWithIndices(text)

  const ignoreLength = terms.filter((term) => term.length < RTL_WORD_LENGTH).length
  const length = terms.length - mentions.length - urls.length - ignoreLength
  if (length <= 0) {
    return false
  }

  const matchLength = terms.filter((term) => term.length >= RTL_WORD_LENGTH && term.match(RTL_CHAR))
    .length
  return matchLength / length > RTL_THRESHOLD
}

const reformatText = (itemObj: Entity) => {
  const { entityText } = itemObj

  if (itemObj.entityType === 'screenName') {
    return LTR_MARK + entityText + RTL_MARK
  }

  if (itemObj.entityType === 'hashtag') {
    return entityText.charAt(1).match(RTL_CHAR) ? entityText : LTR_MARK + entityText
  }

  if (itemObj.entityType === 'url') {
    return entityText + LTR_MARK
  }

  if (itemObj.entityType === 'cashtag') {
    return LTR_MARK + entityText
  }

  return entityText
}

const replaceIndices = (oldText: string) => {
  let lastIndex = 0
  let newText = ''

  twttr.extractEntitiesWithIndices(oldText).forEach((item: twttr.EntityWithIndices) => {
    let type = ''

    if ('screenName' in item && item.screenName) {
      type = 'screenName'
    }
    if ('hashtag' in item && item.hashtag) {
      type = 'hashtag'
    }
    if ('url' in item && item.url) {
      type = 'url'
    }
    if ('cashtag' in item && item.cashtag) {
      type = 'cashtag'
    }

    const respObj: Entity = {
      entityText: oldText.slice(item.indices[0], item.indices[1]),
      entityType: type
    }

    newText += oldText.slice(lastIndex, item.indices[0]) + reformatText(respObj)
    const [, secondItem] = item.indices
    lastIndex = secondItem
  })

  return newText + oldText.slice(lastIndex, oldText.length)
}

const setEmojiMarkers = (plainText: string) => {
  if (!plainText) {
    return plainText
  }

  let text = plainText
  if (plainText.match(RTL_CHAR)) {
    const matchEmoji = plainText.match(EMOJI_MARK)
    const already: string[] = []
    if (matchEmoji) {
      matchEmoji.forEach((emoji) => {
        if (!already || already.includes(emoji)) {
          return
        }

        const rText = emoji + RTL_MARK
        text = text.replace(new RegExp(emoji, 'g'), rText)
        already.push(emoji)
      })
    }
  }
  return text
}

const setParenthesesMarkers = (plainText: string) => {
  if (!plainText) {
    return plainText
  }

  let rText = ')'
  rText += LTR_MARK
  return plainText.replace(CLOSE_PARENTHESES, rText)
}

export const setMarkers = (plainText?: string) => {
  if (!plainText) {
    return plainText
  }

  let text = plainText
  if (plainText.match(RTL_CHAR)) {
    text = replaceIndices(text)
    text = setEmojiMarkers(text)
    text = setParenthesesMarkers(text)
  }
  return text
}

export const shouldAddZWJV2 = (sent: string, offset: number) => {
  if (!sent.match(RTL_CHAR)) {
    return false
  }

  if (offset === 0 || offset === sent.length - 1) {
    return false
  }

  if (!ARABIC_NEXT_RANGE.test(sent.charAt(offset))) {
    return false
  }

  let previous = ''
  for (let i = 1; offset - i >= 0; i += 1) {
    previous = sent.charAt(offset - i)
    if (!ARABIC_DIACRITICS.test(previous)) {
      return ARABIC_PREVIOUS_RANGE.test(previous)
    }
  }

  return false
}

export const rightToLeftStyles = css`
  direction: rtl;
  text-align: right;
  unicode-bidi: embed;
`
